import { useState, useEffect } from "react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import { Select, SelectContent, SelectItem, SelectTrigger, SelectValue } from "@/components/ui/select";
import { X } from "lucide-react";
import { useQuery } from "@tanstack/react-query";
import { base44 } from "@/api/base44Client";

const investorTypes = [
  { value: "holding_company", label: "Holding selskap" },
  { value: "angel_investor", label: "Engle investor" },
  { value: "private_equity", label: "Private Equity" },
  { value: "family_office", label: "Family office" },
  { value: "personal_investor", label: "Personlig investor" },
];

const statuses = [
  { value: "prospect", label: "Prospect" },
  { value: "in_discussion", label: "In Discussion" },
  { value: "committed", label: "Committed" },
  { value: "invested", label: "Invested" },
  { value: "exited", label: "Exited" },
];

const dealStages = [
  { value: "initial_contact", label: "Første kontakt" },
  { value: "qualification", label: "Kvalifisering" },
  { value: "pitch", label: "Pitch" },
  { value: "due_diligence", label: "Due Diligence" },
  { value: "negotiation", label: "Forhandling" },
  { value: "closing", label: "Avslutning" },
  { value: "closed_won", label: "Vunnet" },
  { value: "closed_lost", label: "Tapt" },
];

const getDisplayName = (user) => {
  if (user?.first_name || user?.last_name) {
    return `${user.first_name || ""} ${user.last_name || ""}`.trim();
  }
  return user?.full_name || user?.email || "";
};

export default function InvestorForm({ investor, onSubmit, onClose, isLoading }) {
  const [formData, setFormData] = useState({
    name: "",
    type: "",
    contact_name: "",
    contact_email: "",
    contact_phone: "",
    responsible_person: "",
    responsible_person_name: "",
    status: "prospect",
    deal_stage: "initial_contact",
    notes: "",
  });

  const { data: users = [] } = useQuery({
    queryKey: ["users"],
    queryFn: () => base44.entities.User.list(),
  });

  useEffect(() => {
    if (investor) {
      setFormData({
        name: investor.name || "",
        type: investor.type || "",
        contact_name: investor.contact_name || "",
        contact_email: investor.contact_email || "",
        contact_phone: investor.contact_phone || "",
        responsible_person: investor.responsible_person || "",
        responsible_person_name: investor.responsible_person_name || "",
        status: investor.status || "prospect",
        deal_stage: investor.deal_stage || "initial_contact",
        notes: investor.notes || "",
      });
    }
  }, [investor]);

  const handleSubmit = (e) => {
    e.preventDefault();
    const selectedUser = users.find(u => u.email === formData.responsible_person);
    const submitData = {
      ...formData,
      responsible_person_name: selectedUser ? getDisplayName(selectedUser) : formData.responsible_person_name,
    };
    onSubmit(submitData);
  };

  return (
    <div className="fixed inset-0 bg-black/50 backdrop-blur-sm z-50 flex items-center justify-center p-4">
      <div className="bg-white rounded-2xl w-full max-w-2xl max-h-[90vh] overflow-y-auto">
        <div className="flex items-center justify-between p-6 border-b border-slate-100">
          <h2 className="text-xl font-semibold text-slate-900">
            {investor ? "Edit Investor" : "Add New Investor"}
          </h2>
          <button onClick={onClose} className="p-2 hover:bg-slate-100 rounded-lg transition-colors">
            <X className="w-5 h-5 text-slate-500" />
          </button>
        </div>
        
        <form onSubmit={handleSubmit} className="p-6 space-y-5">
          <div className="grid grid-cols-1 md:grid-cols-2 gap-5">
            <div className="space-y-2">
              <Label htmlFor="name">Investor Name *</Label>
              <Input
                id="name"
                value={formData.name}
                onChange={(e) => setFormData({ ...formData, name: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="type">Investor Type</Label>
              <Select value={formData.type} onValueChange={(v) => setFormData({ ...formData, type: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Select type" />
                </SelectTrigger>
                <SelectContent>
                  {investorTypes.map((t) => (
                    <SelectItem key={t.value} value={t.value}>{t.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_name">Contact Name</Label>
              <Input
                id="contact_name"
                value={formData.contact_name}
                onChange={(e) => setFormData({ ...formData, contact_name: e.target.value })}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_email">Contact Email *</Label>
              <Input
                id="contact_email"
                type="email"
                value={formData.contact_email}
                onChange={(e) => setFormData({ ...formData, contact_email: e.target.value })}
                required
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="contact_phone">Contact Phone</Label>
              <Input
                id="contact_phone"
                value={formData.contact_phone}
                onChange={(e) => setFormData({ ...formData, contact_phone: e.target.value })}
                className="h-11"
              />
            </div>
            <div className="space-y-2">
              <Label htmlFor="status">Status</Label>
              <Select value={formData.status} onValueChange={(v) => setFormData({ ...formData, status: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {statuses.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="responsible">Ansvarlig person</Label>
              <Select value={formData.responsible_person} onValueChange={(v) => setFormData({ ...formData, responsible_person: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue placeholder="Velg ansvarlig" />
                </SelectTrigger>
                <SelectContent>
                  {users.map((u) => (
                    <SelectItem key={u.id} value={u.email}>{getDisplayName(u)}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
            <div className="space-y-2">
              <Label htmlFor="deal_stage">Deal Stage</Label>
              <Select value={formData.deal_stage} onValueChange={(v) => setFormData({ ...formData, deal_stage: v })}>
                <SelectTrigger className="h-11">
                  <SelectValue />
                </SelectTrigger>
                <SelectContent>
                  {dealStages.map((s) => (
                    <SelectItem key={s.value} value={s.value}>{s.label}</SelectItem>
                  ))}
                </SelectContent>
              </Select>
            </div>
          </div>
          
          <div className="space-y-2">
            <Label htmlFor="notes">Notes</Label>
            <Textarea
              id="notes"
              value={formData.notes}
              onChange={(e) => setFormData({ ...formData, notes: e.target.value })}
              rows={3}
            />
          </div>
          
          <div className="flex justify-end gap-3 pt-4">
            <Button type="button" variant="outline" onClick={onClose}>Cancel</Button>
            <Button type="submit" disabled={isLoading} className="bg-slate-900 hover:bg-slate-800">
              {isLoading ? "Saving..." : investor ? "Update Investor" : "Add Investor"}
            </Button>
          </div>
        </form>
      </div>
    </div>
  );
}